"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Input,
  ScrollShadow,
  Listbox,
  ListboxItem,
  ListboxSection,
  cn,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { ChatHistoryMetadata } from "@webuild-ai/admin/models/components";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { blurActiveElement } from "@/utils/dom";
import { logger } from "@/telemetry";

interface ChatHistorySearchModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectChat: (chatId: string) => void;
  chatHistory: ChatHistoryMetadata[]; // Fallback: local chat records for when no search term
}

/**
 * ChatHistorySearchModal component for searching through chat history
 * with backend-powered search in both summary and message content
 *
 * Search Strategy:
 * - Uses backend API search endpoint for full-text search
 * - Searches both summary (title) and message content fields
 * - Debounced search with 500ms delay for better UX
 * - Falls back to local chatHistory when no search term
 */
export function ChatHistorySearchModal({
  isOpen,
  onOpenChange,
  onSelectChat,
  chatHistory,
}: ChatHistorySearchModalProps) {
  const { t } = useTranslation();
  const { adminClient } = useWebuildAPIClient();

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredChats, setFilteredChats] = useState<ChatHistoryMetadata[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  // Time ranges for grouping
  const timeRanges = useMemo(
    () => [
      { title: t("Today"), days: 1 },
      { title: t("Last 7 Days"), days: 7 },
      { title: t("Last 30 Days"), days: 30 },
      { title: t("Older"), days: Number.MAX_SAFE_INTEGER },
    ],
    [t],
  );

  // Backend search function
  const performSearch = useCallback(
    async (term: string) => {
      logger.info("🔍 performSearch called", { term });

      if (!term.trim()) {
        // No search term - show local chatHistory
        logger.info("📝 No search term, showing local chatHistory", {
          chatCount: chatHistory.length,
        });
        setFilteredChats(chatHistory);
        setSelectedIndex(chatHistory.length > 0 ? 0 : -1);
        setIsSearching(false);

        return;
      }

      if (!adminClient) {
        logger.error("❌ Admin client not available");
        setFilteredChats([]);
        setIsSearching(false);

        return;
      }

      try {
        setIsSearching(true);
        logger.info("🚀 Calling searchChatHistories API", { query: term });

        const results = await adminClient.chatHistories.searchChatHistories({
          query: term,
          limit: 100,
          searchInContent: true, // Search in both summary and message content
        });

        logger.info("✅ Search results received", { resultCount: results.length });

        setFilteredChats(results);
        setSelectedIndex(results.length > 0 ? 0 : -1);
      } catch (error) {
        logger.error("❌ Error searching chat history", error);
        // Fallback to empty results on error
        setFilteredChats([]);
        setSelectedIndex(-1);
      } finally {
        setIsSearching(false);
      }
    },
    [adminClient, chatHistory],
  );

  // Debounced search with 500ms delay
  const debouncedSearch = useMemo(() => {
    return (term: string) => {
      logger.info("⏱️ debouncedSearch called", { term });
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Show loading state immediately
      if (term.trim()) {
        setIsSearching(true);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        performSearch(term);
      }, 500);
    };
  }, [performSearch]);

  // Handle search input changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Clear search term
  const handleClearSearch = () => {
    setSearchTerm("");
    setFilteredChats(chatHistory);
    setSelectedIndex(chatHistory.length > 0 ? 0 : -1);
  };

  // Handle modal close with focus cleanup
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      blurActiveElement();
    }
    onOpenChange(open);
  };

  // Handle chat selection
  const handleChatSelect = (chatId: string) => {
    onSelectChat(chatId);
    handleOpenChange(false);
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const chatIds = filteredChats.map((chat) => chat.id);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < chatIds.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Home":
        e.preventDefault();
        setSelectedIndex(0);
        break;
      case "End":
        e.preventDefault();
        setSelectedIndex(chatIds.length - 1);
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && chatIds[selectedIndex]) {
          handleChatSelect(chatIds[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        handleOpenChange(false);
        break;
    }
  };

  // Initialize modal state when opened
  useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
      setFilteredChats(chatHistory);
      setSelectedIndex(chatHistory.length > 0 ? 0 : -1);
      setIsSearching(false);
    }

    // Cleanup debounce timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [isOpen, chatHistory]);

  // Group chats by time range
  const groupedChats = useMemo(() => {
    return timeRanges
      .map(({ title, days }) => {
        const chatsInRange = filteredChats
          .filter((item) => {
            const itemDate = new Date(item.updatedAt);
            const now = new Date();
            const diffTime = Math.abs(now.getTime() - itemDate.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            return diffDays <= days;
          })
          .filter((item) => {
            // Only include items that haven't been shown in a more recent group
            const itemDate = new Date(item.updatedAt);
            const now = new Date();
            const diffTime = Math.abs(now.getTime() - itemDate.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            // Check if this item belongs to a more recent group
            const moreRecentGroup = timeRanges.find(
              (range) => range.days < days && diffDays <= range.days,
            );

            return !moreRecentGroup;
          })
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

        return chatsInRange.length > 0 ? { title, chats: chatsInRange } : null;
      })
      .filter(Boolean) as { title: string; chats: ChatHistoryMetadata[] }[];
  }, [filteredChats, timeRanges]);

  // Calculate total result count for screen reader announcement
  const resultCount = filteredChats.length;
  const hasResults = resultCount > 0;

  return (
    <Modal
      aria-labelledby="search-modal-header"
      aria-modal="true"
      classNames={{
        closeButton: "top-3 right-3 p-0 hover:bg-default-100",
      }}
      data-id="chat-history-search-modal"
      isOpen={isOpen}
      role="dialog"
      size="4xl"
      onOpenChange={handleOpenChange}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader
              className="flex flex-col gap-1"
              data-id="chat-history-search-modal-header"
              id="search-modal-header"
            >
              {t("Search chat history")}
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4" onKeyDown={handleKeyDown}>
                {/* Search Input */}
                <Input
                  ref={inputRef}
                  autoFocus
                  aria-label={t("Search chat history")}
                  autoComplete="off"
                  className="w-full"
                  data-id="chat-search-input"
                  endContent={
                    searchTerm && (
                      <button
                        aria-label={t("Clear search")}
                        className="focus:outline-none"
                        data-id="chat-search-clear-button"
                        type="button"
                        onClick={handleClearSearch}
                      >
                        <Icon
                          className="text-default-400"
                          icon="solar:close-circle-linear"
                          width={20}
                        />
                      </button>
                    )
                  }
                  placeholder={t("Search by title or content...")}
                  startContent={
                    <Icon className="text-default-400" icon="solar:magnifer-linear" width={20} />
                  }
                  type="text"
                  value={searchTerm}
                  onValueChange={handleSearchChange}
                />

                {/* Result count announcement for screen readers */}
                <div aria-live="polite" className="sr-only">
                  {hasResults
                    ? t("{{count}} results found", { count: resultCount })
                    : t("No results found")}
                </div>

                {/* Results Display */}
                <div className="flex flex-col gap-2">
                  {isSearching ? (
                    // Loading State
                    <div
                      className="flex flex-col items-center justify-center py-12 gap-4 text-center"
                      data-id="search-loading"
                    >
                      <div className="relative">
                        <Icon
                          className="text-brand-primary animate-spin"
                          icon="solar:refresh-circle-linear"
                          width={48}
                        />
                      </div>
                      <div className="flex flex-col gap-3">
                        <p className="text-lg font-medium text-default-600">{t("Searching...")}</p>
                        <p className="text-sm text-default-400">
                          {t("Searching through your chat history")}
                        </p>
                      </div>
                    </div>
                  ) : hasResults ? (
                    // Results section
                    <div className="relative">
                      <ScrollShadow
                        ref={listboxRef}
                        className="w-full max-h-[50vh] overflow-y-auto"
                        data-id="chat-search-results-container"
                      >
                        <Listbox
                          aria-label={t("Search results")}
                          className="p-0"
                          data-id="chat-search-results-list"
                          role="listbox"
                          variant="flat"
                          onAction={(key) => {
                            if (key) {
                              handleChatSelect(key as string);
                            }
                          }}
                        >
                          {groupedChats.map(({ title, chats }) => (
                            <ListboxSection
                              key={title}
                              classNames={{
                                base: "py-0",
                                heading:
                                  "py-2 pl-[10px] text-small text-brand-primary font-bold dark:!text-foreground border-b border-divider mb-1",
                              }}
                              data-id={`chat-search-section-${title}`}
                              title={title}
                            >
                              {chats.map((chat) => {
                                const globalIndex = filteredChats.findIndex(
                                  (c) => c.id === chat.id,
                                );
                                const isSelected = globalIndex === selectedIndex;

                                return (
                                  <ListboxItem
                                    key={chat.id}
                                    aria-selected={isSelected}
                                    className={cn(
                                      "h-[44px] px-[12px] py-[10px] text-default-500 cursor-pointer",
                                      "hover:bg-default-200",
                                      "data-[focus-visible=true]:outline-offset-[-2px]",
                                      {
                                        "bg-default-100": isSelected,
                                      },
                                    )}
                                    data-id={`chat-search-result-${chat.id}`}
                                    role="option"
                                    textValue={chat.summary || "Chat history item"}
                                  >
                                    <span
                                      className="block truncate max-w-full whitespace-nowrap overflow-hidden"
                                      title={chat.summary}
                                    >
                                      {chat.summary}
                                    </span>
                                  </ListboxItem>
                                );
                              })}
                            </ListboxSection>
                          ))}
                        </Listbox>
                      </ScrollShadow>
                    </div>
                  ) : (
                    // Empty State
                    <div
                      className="flex flex-col items-center justify-center py-12 gap-4 text-center"
                      data-id="search-no-results"
                    >
                      <Icon className="text-default-300" icon="solar:magnifer-linear" width={48} />
                      <div className="flex flex-col gap-3">
                        <p className="text-lg font-medium text-default-600">
                          {t("No results found")}
                        </p>
                        <p className="text-sm text-default-400">
                          {searchTerm
                            ? t("Try different search terms or check your spelling")
                            : t("Start typing to search your chat history")}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
